import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { uploadImage } from '../firebase/config';
import Layout from '../components/Layout';
import SignInModal from '../components/SignInModal';
import SEO from '../components/SEO';
import TemplateCardPreview from '../components/TemplateCardPreview';
import {
  Search, ShoppingCart, Loader2, X,
  Upload, ImagePlus, FileImage, Package,
  AlertCircle, FileSpreadsheet, Minus, Plus,
  ChevronRight, Sparkles, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to calculate discount percentage
const calculateDiscount = (mrp, price) => {
  if (!mrp || !price || mrp <= price) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
};

/* ══════════════════════════════════════════════════════════════
   SHOP PAGE - Upload Design, Custom Designs, Ready-Made Cards
══════════════════════════════════════════════════════════════ */

const Shop = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  // Data
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]); // Ready-made products from admin
  const [categories, setCategories] = useState([]);
  const [discountTiers, setDiscountTiers] = useState([]);
  const [globalPricing, setGlobalPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDesignsModal, setShowDesignsModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef(null);

  // Load Data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRes, productsRes, categoriesRes, discountsRes, pricingRes] = await Promise.all([
        fetch(`${API_URL}/api/card-designer/templates`),
        fetch(`${API_URL}/api/shop/products`),
        fetch(`${API_URL}/api/card-designer/categories`),
        fetch(`${API_URL}/api/shop/quantity-discounts`),
        fetch(`${API_URL}/api/shop/global-pricing`)
      ]);

      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      if (categoriesRes.ok) setCategories(await categoriesRes.json());
      if (discountsRes.ok) setDiscountTiers(await discountsRes.json());
      if (pricingRes.ok) setGlobalPricing(await pricingRes.json());
    } catch (e) {
      console.error('Failed to load shop data:', e);
    } finally {
      setLoading(false);
    }
  };

  // Search
  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/card-designer/templates/search?q=${encodeURIComponent(value)}`);
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.results || []);
        }
      } catch (e) {
        console.error('Search error:', e);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  // Add to cart (for ready-made products)
  const addToCart = async (product) => {
    if (!user) {
      setShowSignIn(true);
      return;
    }

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/shop/cart/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: product.id,
          quantity: 1
        })
      });

      if (res.ok) {
        toast({ title: 'Added to cart', description: product.name });
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail, variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Handle template click - redirect to User Template Editor
  const handleTemplateClick = (template) => {
    // Navigate to user template editor with category and template slug
    const categorySlug = template.category_slug || 'templates';
    const templateSlug = template.slug || template.id;
    navigate(`/shop/${categorySlug}/${templateSlug}`);
  };

  // Display templates based on search or default
  const displayTemplates = searchQuery ? searchResults : templates;
  
  // Get top 4 custom designs (from search results if searching, otherwise from all)
  const topDesigns = displayTemplates.slice(0, 4);

  // Filter by category
  const [selectedCategory, setSelectedCategory] = useState(null);
  const filteredTemplates = selectedCategory
    ? displayTemplates.filter(t => t.category_name === selectedCategory || t.category === selectedCategory)
    : displayTemplates;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title="Shop | Piper Card" description="Order custom business cards with bulk discounts" />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Find Your Perfect Card</h1>
            <p className="text-gray-500">Search by profession, name or style — handles typos too</p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto mt-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="e.g. Doctor, Lawyer, Real Estate, Startup..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-12 h-12 text-base rounded-full border-gray-200 shadow-sm"
                />
                {isSearching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-violet-500 animate-spin" />}
              </div>
            </div>

            {/* Quick Category Tags */}
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {categories.slice(0, 8).map(cat => (
                <button
                  key={cat.id}
                  onClick={() => { setSelectedCategory(cat.name); setShowDesignsModal(true); }}
                  className="px-4 py-1.5 bg-white rounded-full text-sm text-gray-600 hover:bg-violet-50 hover:text-violet-600 border border-gray-200 transition-all"
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════
              SECTION 1: Upload Your Own Design
          ════════════════════════════════════════════════════════ */}
          <div className="mb-10">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-6 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                    <Upload className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold mb-1">Upload Your Own Design</h2>
                    <p className="text-violet-100 text-sm">Have your own business card design? Upload it and we'll print it for you.</p>
                    <p className="text-violet-200 text-xs mt-1">JPG, PNG, PDF, WebP • Max 10 MB</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowUploadModal(true)}
                  className="bg-white text-violet-600 hover:bg-violet-50 font-semibold px-8"
                  data-testid="upload-design-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Design
                </Button>
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════
              SECTION 2: Custom Designs (Top 4 + Explore More)
          ════════════════════════════════════════════════════════ */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Custom NFC Card Designs</h2>
                <p className="text-sm text-gray-500">Pick a template and personalize it with your details</p>
              </div>
              <Button
                variant="outline"
                onClick={() => { setSelectedCategory(null); setShowDesignsModal(true); }}
                className="text-violet-600 border-violet-200 hover:bg-violet-50"
                data-testid="explore-designs-btn"
              >
                Explore All {templates.length}+ Designs
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Global NFC Price Banner */}
            {globalPricing && (
              <div className="mb-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                        <path d="M8 12a4 4 0 0 1 8 0"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">All NFC Cards - One Price</p>
                      <p className="text-sm text-gray-500">Choose any design template, same price</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {globalPricing.nfc_mrp > globalPricing.nfc_price && (
                      <p className="text-sm text-gray-400 line-through">₹{globalPricing.nfc_mrp}</p>
                    )}
                    <p className="text-2xl font-bold text-violet-600">₹{globalPricing.nfc_price}</p>
                    {globalPricing.nfc_mrp > globalPricing.nfc_price && (
                      <p className="text-xs text-emerald-600 font-semibold">
                        {Math.round(((globalPricing.nfc_mrp - globalPricing.nfc_price) / globalPricing.nfc_mrp) * 100)}% OFF
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {topDesigns.map((template, idx) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateClick(template)}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-violet-300 hover:shadow-lg transition-all text-left"
                  data-testid={`custom-design-${idx}`}
                >
                  <div className="aspect-[1.6/1] bg-gray-100 relative overflow-hidden">
                    {template.thumbnail_image ? (
                      <img 
                        src={template.thumbnail_image} 
                        alt={template.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <TemplateCardPreview template={template} side="front" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-gray-900 text-sm truncate">{template.name}</p>
                    <p className="text-xs text-gray-500 truncate">{template.category_name || template.category}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════
              SECTION 3: Ready-Made Cards (Direct to Cart)
          ════════════════════════════════════════════════════════ */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Ready-Made NFC Cards</h2>
                <p className="text-sm text-gray-500">Pre-designed cards • Add to cart directly</p>
              </div>
            </div>

            {products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product, idx) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all"
                    data-testid={`readymade-card-${idx}`}
                  >
                    <div className="aspect-[1.6/1] bg-gray-100 relative overflow-hidden">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      {calculateDiscount(product.mrp, product.price) > 0 && (
                        <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                          {calculateDiscount(product.mrp, product.price)}% OFF
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                      <p className="text-xs text-gray-500 truncate">{product.category}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            {calculateDiscount(product.mrp, product.price) > 0 && (
                              <span className="text-xs text-gray-400 line-through">₹{product.mrp}</span>
                            )}
                            <span className="text-sm font-bold text-violet-600">₹{product.price}</span>
                          </div>
                          {calculateDiscount(product.mrp, product.price) > 0 && (
                            <span className="text-[10px] text-emerald-600 font-medium">Save ₹{product.mrp - product.price}</span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => addToCart(product)}
                          className="h-8 px-3 bg-violet-600 hover:bg-violet-700"
                          data-testid={`add-to-cart-btn-${idx}`}
                        >
                          <ShoppingCart className="w-3.5 h-3.5 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No ready-made cards available yet</p>
              </div>
            )}
          </div>

          {/* Bulk Discount Info Banner */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-emerald-800 mb-2">Bulk Order Discounts</h3>
                <p className="text-sm text-emerald-700 mb-3">Order more, save more! Get up to 50% off on bulk orders.</p>
                <div className="flex flex-wrap gap-2">
                  {discountTiers.filter(t => t.discount_percent > 0).map((tier, i) => (
                    <span key={i} className="bg-white px-3 py-1 rounded-full text-xs font-medium text-emerald-700 border border-emerald-200">
                      {tier.min_quantity}{tier.max_quantity ? `-${tier.max_quantity}` : '+'} cards: {tier.discount_percent}% off
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* SEO-Friendly About Section */}
          <article className="mt-12 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" itemScope itemType="https://schema.org/Product">
            <header className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6">
              <h2 className="text-2xl font-bold mb-2" itemProp="name">Premium NFC Smart Business Cards</h2>
              <p className="text-gray-300 text-sm" itemProp="description">
                Smart NFC cards designed to make lasting impressions. Tap & share your digital profile instantly.
              </p>
            </header>

            <div className="p-6 space-y-8">
              {/* NFC Smart Cards */}
              <section className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                        <path d="M8 12a4 4 0 0 1 8 0"/>
                        <path d="M6 12a6 6 0 0 1 12 0"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">NFC Smart Business Cards</h3>
                      <p className="text-sm text-gray-600">Tap & Share Digital Profile Instantly</p>
                    </div>
                  </div>
                  {globalPricing && (
                    <div className="text-right">
                      {globalPricing.nfc_mrp > globalPricing.nfc_price && (
                        <p className="text-sm text-gray-400 line-through">₹{globalPricing.nfc_mrp}</p>
                      )}
                      <p className="text-2xl font-bold text-blue-600">₹{globalPricing.nfc_price}</p>
                      {globalPricing.nfc_mrp > globalPricing.nfc_price && (
                        <p className="text-xs text-emerald-600 font-semibold">
                          {Math.round(((globalPricing.nfc_mrp - globalPricing.nfc_price) / globalPricing.nfc_mrp) * 100)}% OFF
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                <p className="text-gray-700 mb-4">
                  Modern, smart, and instantly shareable — the future of networking. NFC-enabled smart business cards allow instant profile sharing with just a tap.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/70 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3">Product Details</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex justify-between"><span>Size:</span><span className="font-medium text-gray-800">85.6 x 54 mm (Credit card)</span></li>
                      <li className="flex justify-between"><span>Material:</span><span className="font-medium text-gray-800">High-quality PVC</span></li>
                      <li className="flex justify-between"><span>Finish:</span><span className="font-medium text-gray-800">Matte UV Print</span></li>
                      <li className="flex justify-between"><span>Technology:</span><span className="font-medium text-gray-800">Built-in NFC Chip</span></li>
                      <li className="flex justify-between"><span>Design:</span><span className="font-medium text-gray-800">Fully Customizable</span></li>
                    </ul>
                  </div>
                  <div className="bg-white/70 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3">Why Choose NFC Cards?</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Instant contact sharing (no app required)
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Eco-friendly & reusable
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Perfect for tech-savvy professionals
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Long-lasting and waterproof
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Works seamlessly with Android & iPhone
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Disclaimer */}
              <footer className="text-center text-sm text-gray-500 pt-4 border-t border-gray-100">
                <p className="flex items-center justify-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>Note: We do not accept designs that belong to or represent government or government-affiliated organizations.</span>
                </p>
              </footer>
            </div>
          </article>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          UPLOAD DESIGN MODAL
      ════════════════════════════════════════════════════════ */}
      {showUploadModal && (
        <UploadDesignModal
          user={user}
          discountTiers={discountTiers}
          onClose={() => setShowUploadModal(false)}
          onSignIn={() => { setShowUploadModal(false); setShowSignIn(true); }}
        />
      )}

      {/* ════════════════════════════════════════════════════════
          EXPLORE DESIGNS MODAL
      ════════════════════════════════════════════════════════ */}
      {showDesignsModal && (
        <DesignsModal
          templates={filteredTemplates}
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          isSearching={isSearching}
          onSelectTemplate={handleTemplateClick}
          onClose={() => setShowDesignsModal(false)}
        />
      )}

      {/* Sign In Modal */}
      <SignInModal open={showSignIn} onOpenChange={setShowSignIn} />
    </Layout>
  );
};


/* ══════════════════════════════════════════════════════════════
   UPLOAD DESIGN MODAL
══════════════════════════════════════════════════════════════ */
const UploadDesignModal = ({ user, discountTiers, onClose, onSignIn }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [designFiles, setDesignFiles] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);

  const basePrice = 999;

  // Calculate pricing
  const calculatePricing = () => {
    let quantityDiscountPercent = 0;
    let tierName = '1 card';
    for (const tier of discountTiers) {
      const minQ = tier.min_quantity;
      const maxQ = tier.max_quantity;
      if (maxQ === null) {
        if (quantity >= minQ) {
          quantityDiscountPercent = tier.discount_percent;
          tierName = `${minQ}+`;
        }
      } else if (quantity >= minQ && quantity <= maxQ) {
        quantityDiscountPercent = tier.discount_percent;
        tierName = `${minQ}-${maxQ}`;
        break;
      }
    }

    const subtotal = basePrice * quantity;
    const discountAmount = Math.round(subtotal * (quantityDiscountPercent / 100));
    const total = subtotal - discountAmount;

    return { basePrice, quantity, subtotal, quantityDiscountPercent, discountAmount, total, tierName };
  };

  const pricing = calculatePricing();

  const handleDesignFiles = (files) => {
    const arr = Array.from(files);
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    for (const f of arr) {
      if (!allowed.includes(f.type)) { setError(`'${f.name}' is not supported.`); return; }
      if (f.size > 10 * 1024 * 1024) { setError(`'${f.name}' exceeds 10 MB.`); return; }
    }
    setError('');
    setDesignFiles(prev => [...prev, ...arr].slice(0, 2));
  };

  const handleCsvFile = (files) => {
    const file = files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { setError('Please upload a CSV file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('CSV exceeds 10 MB.'); return; }
    setError('');
    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split('\n').filter(l => l.trim());
      const rowCount = lines.length - 1;
      if (rowCount >= 1) setQuantity(rowCount);
    };
    reader.readAsText(file);
  };

  const handleProceed = async () => {
    if (!user) { onSignIn(); return; }
    if (designFiles.length === 0) { setError('Please upload your design file.'); return; }

    setSubmitting(true);
    try {
      const token = await user.getIdToken();
      
      // Upload design files to Firebase Storage
      const imageUrls = [];
      for (let i = 0; i < designFiles.length; i++) {
        const file = designFiles[i];
        const timestamp = Date.now();
        const storagePath = `user-designs/${user.uid}/${timestamp}_${i}_${file.name}`;
        const url = await uploadImage(file, storagePath);
        imageUrls.push(url);
      }
      
      // Upload CSV to Firebase Storage if provided
      let csvUrl = null;
      if (csvFile) {
        const csvPath = `user-designs/${user.uid}/${Date.now()}_bulk_order.csv`;
        csvUrl = await uploadImage(csvFile, csvPath);
      }
      
      // Send Firebase URLs to backend
      const res = await fetch(`${API_URL}/api/shop/cart/add-design`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity: quantity,
          image_urls: imageUrls,
          csv_url: csvUrl
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to add to cart');
      }

      toast({ title: 'Added to cart!', description: `${quantity} card(s) added to your cart.` });
      onClose();
      navigate('/cart');
    } catch (e) {
      console.error('Upload error:', e);
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
              <Upload className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Upload Your Design</h2>
              <p className="text-xs text-gray-500">Get printed cards delivered</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-5">
          {/* Design Files */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2"><ImagePlus className="w-4 h-4 inline mr-1" />Upload Design *</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${designFiles.length > 0 ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-violet-300'}`}
            >
              <input ref={fileInputRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={e => handleDesignFiles(e.target.files)} />
              {designFiles.length > 0 ? (
                <div className="space-y-2">{designFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-center gap-2 text-sm text-emerald-700">
                    <FileImage className="w-4 h-4" /><span>{f.name}</span>
                    <button onClick={e => { e.stopPropagation(); setDesignFiles(prev => prev.filter((_, idx) => idx !== i)); }}><X className="w-4 h-4 text-red-500" /></button>
                  </div>
                ))}</div>
              ) : (
                <><Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-600">Click to upload (JPG, PNG, PDF)</p><p className="text-xs text-gray-400 mt-1">Max 10 MB</p></>
              )}
            </div>
          </div>

          {/* Quantity */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity</label>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}><Minus className="w-4 h-4" /></Button>
              <Input type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="w-20 text-center text-lg font-bold" />
              <Button variant="outline" size="icon" onClick={() => setQuantity(quantity + 1)}><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Discount Tiers */}
          <div className="bg-violet-50 rounded-xl p-3 mb-4">
            <p className="text-xs font-bold text-gray-700 mb-2">Bulk Discounts</p>
            <div className="flex flex-wrap gap-2">
              {discountTiers.map((tier, i) => (
                <span key={i} className={`px-2 py-1 rounded-full text-xs font-medium ${pricing.tierName === (tier.max_quantity ? `${tier.min_quantity}-${tier.max_quantity}` : `${tier.min_quantity}+`) ? 'bg-violet-600 text-white' : 'bg-white text-gray-600'}`}>
                  {tier.min_quantity}{tier.max_quantity ? `-${tier.max_quantity}` : '+'}: {tier.discount_percent}% off
                </span>
              ))}
            </div>
          </div>

          {/* CSV for bulk orders - Optional */}
          {quantity > 1 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Bulk Order Data (Optional)</span>
              </div>
              <p className="text-xs text-blue-600 mb-3">Provide personalized data for each card</p>
              
              {/* Download Simple Template */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  const csvContent = 'Name,Title,Company,Email,Phone\nJohn Doe,Manager,Acme Inc,john@acme.com,9876543210';
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'bulk_order_template.csv';
                  a.click();
                  URL.revokeObjectURL(url);
                  toast({ title: 'CSV template downloaded', description: 'Fill in your data for each card' });
                }}
                className="w-full mb-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                <Download className="w-3 h-3 mr-1.5" />
                Download CSV Template
              </Button>
              
              <input ref={csvInputRef} type="file" accept=".csv" className="hidden" onChange={e => handleCsvFile(e.target.files)} />
              {csvFile ? (
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <FileSpreadsheet className="w-4 h-4" /><span className="truncate max-w-[120px]">{csvFile.name}</span><span className="text-xs text-gray-500">({quantity} rows)</span>
                  </div>
                  <button onClick={e => { e.stopPropagation(); setCsvFile(null); }}><X className="w-4 h-4 text-red-500" /></button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => csvInputRef.current?.click()}
                  className="w-full text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <Upload className="w-3 h-3 mr-1.5" />
                  Upload CSV (Optional)
                </Button>
              )}
              <p className="text-xs text-blue-600 mt-2 text-center">We'll contact you after payment if CSV not provided</p>
            </div>
          )}

          {/* Price Summary */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">{quantity} × ₹{pricing.basePrice}</span><span>₹{pricing.subtotal.toLocaleString()}</span></div>
              {pricing.quantityDiscountPercent > 0 && (
                <div className="flex justify-between text-emerald-600"><span>Discount ({pricing.quantityDiscountPercent}%)</span><span>-₹{pricing.discountAmount.toLocaleString()}</span></div>
              )}
              <div className="flex justify-between pt-2 border-t text-lg"><span className="font-bold">Total</span><span className="font-bold text-violet-600">₹{pricing.total.toLocaleString()}</span></div>
            </div>
          </div>

          {error && <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3"><AlertCircle className="w-4 h-4" />{error}</div>}

          <Button onClick={handleProceed} disabled={submitting || designFiles.length === 0} className="w-full bg-violet-600 hover:bg-violet-700">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue</>}
          </Button>
          
          <p className="text-xs text-gray-400 text-center mt-3">Contact details will be collected at checkout</p>
        </div>
      </div>
    </div>
  );
};


/* ══════════════════════════════════════════════════════════════
   DESIGNS MODAL (Explore All)
══════════════════════════════════════════════════════════════ */
const DesignsModal = ({ templates, categories, selectedCategory, onCategoryChange, searchQuery, onSearch, isSearching, onSelectTemplate, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white z-10 px-5 py-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Explore All Designs</h2>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search designs..." value={searchQuery} onChange={e => onSearch(e.target.value)} className="pl-10" />
            {isSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-violet-500 animate-spin" />}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => onCategoryChange(null)} className={`px-3 py-1 rounded-full text-xs font-medium ${!selectedCategory ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'}`}>All</button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => onCategoryChange(cat.name)} className={`px-3 py-1 rounded-full text-xs font-medium ${selectedCategory === cat.name ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{cat.name}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {templates.map(template => (
              <button
                key={template.id}
                onClick={() => onSelectTemplate(template)}
                className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-violet-300 hover:shadow-lg transition-all text-left"
              >
                <div className="aspect-[1.6/1] bg-gray-100 relative overflow-hidden">
                  {template.thumbnail_image ? (
                    <img 
                      src={template.thumbnail_image} 
                      alt={template.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <TemplateCardPreview template={template} side="front" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  )}
                </div>
                <div className="p-3">
                  <p className="font-medium text-gray-900 text-sm truncate">{template.name}</p>
                  <p className="text-xs text-gray-500">{template.category_name || template.category}</p>
                </div>
              </button>
            ))}
          </div>
          {templates.length === 0 && (
            <div className="text-center py-12"><Package className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No designs found</p></div>
          )}
        </div>
      </div>
    </div>
  );
};


export default Shop;
